/**
 * QahwaHub Manager — visual language (warm coffeehouse, not generic gray “delivery admin”).
 * Use these tokens in BaristaDashboard only.
 */
export const qhShell = {
  page: 'relative flex min-h-screen overflow-x-hidden bg-[#fbf4ec] text-[#2a2420] max-lg:flex-col dark:bg-[#16110d] dark:text-[#efe6da]',
  sidebar:
    'flex w-[276px] max-w-[85vw] shrink-0 flex-col overflow-y-auto border-r border-[#dcc8b2] bg-[#efe2d2] text-[#4f311f] shadow-[4px_0_28px_rgba(44,31,22,0.08)] fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out lg:static lg:z-auto lg:max-w-none lg:translate-x-0 lg:overflow-y-hidden dark:border-[#3a2d25] dark:bg-[#120e0b] dark:text-[#ede4d7]',
  sidebarOpen: 'translate-x-0',
  sidebarClosed: '-translate-x-full lg:translate-x-0',
  mobileBar:
    'sticky top-0 z-30 flex items-center gap-3 border-b border-[#dcc8b2] bg-[#efe2d2] px-3 py-3 lg:hidden dark:border-[#3a2d25] dark:bg-[#120e0b]',
  menuBtn:
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#fff9f1] text-[#4a2e1f] shadow-sm ring-1 ring-[#d8c5b0] touch-manipulation dark:bg-[#231e1a] dark:text-[#f0e8df] dark:ring-[#3a332d]',
  logoMark:
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#e8c08a] via-[#b97845] to-[#6b3410] text-xl shadow-lg ring-1 ring-white/15',
  brandBlock: 'min-w-0 flex-1',
  brandTitle:
    "font-['Fraunces',Georgia,serif] text-[1.15rem] font-semibold leading-tight tracking-tight text-[#4a2e1f] dark:text-[#f5eadf]",
  brandTagline: 'text-[11px] font-medium text-[#8a7058] tracking-wide dark:text-[#9d8c7f]',
  cafeLabel: 'mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-[#7c6652] dark:text-[#8e7a6b]',
  cafeSelect:
    'mt-1.5 w-full cursor-pointer rounded-xl border border-[#d8c5b0] bg-[#fff9f1] px-3 py-2.5 text-sm text-[#4a2e1f] focus:border-[#b97845] focus:outline-none focus:ring-2 focus:ring-[#b97845]/40 touch-manipulation dark:border-[#3a332d] dark:bg-[#231e1a] dark:text-[#f0e8df]',
  signOut:
    'mt-3 w-full text-left text-xs font-semibold text-[#6d4e38] underline underline-offset-2 hover:text-[#4a2e1f] dark:text-[#c4a882] dark:hover:text-[#fff5eb]',
  staffFieldSelect:
    'w-full cursor-pointer rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 touch-manipulation',
  marketingTierCard: (selected: boolean) =>
    `rounded-xl border px-3 py-3 text-left transition-all duration-200 touch-manipulation ${
      selected
        ? 'border-[#b9561c] bg-gradient-to-br from-[#fff8f0] to-white shadow-md ring-2 ring-[#c45c26]/35'
        : 'border-[#e8dcc8] bg-white/80 hover:border-[#d4b896] hover:bg-white hover:shadow-sm'
    }`,
  navScroll: 'flex-1 overflow-y-auto px-2 pb-6 pt-2 max-lg:flex-none max-lg:overflow-visible',
  navGroupLabel:
    'px-3 pb-1 pt-5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#8a7058] dark:text-[#7f6f63] first:pt-3',
  navRow: (active: boolean) =>
    `group w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm font-medium transition-all duration-200 ${
      active
        ? 'bg-[#b97845]/18 text-[#4a2e1f] ring-1 ring-[#b97845]/30 shadow-sm dark:bg-[#b97845]/22 dark:text-[#fff5eb] dark:ring-[#b97845]/45 dark:shadow-black/10'
        : 'text-[#6d4e38] hover:bg-[#f7ecdf] hover:text-[#4a2e1f] dark:text-[#b0a090] dark:hover:bg-white/[0.06] dark:hover:text-[#f2ebe3]'
    }`,
  navIconBox: (active: boolean) =>
    `flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[15px] transition-colors ${
      active
        ? 'bg-[#b97845] text-white shadow-md shadow-black/25'
        : 'bg-[#e9dccd] text-[#9b6b45] group-hover:bg-[#ddccb9] group-hover:text-[#7b4b28] dark:bg-[#29231e] dark:text-[#c4a882] dark:group-hover:bg-[#342c26] dark:group-hover:text-[#e8d4bc]'
    }`,
  chevron: (open: boolean) =>
    `text-[10px] text-[#8a7058] dark:text-[#6e635a] transition-transform duration-200 ${open ? 'rotate-180' : ''}`,
  subNavWrap: 'ml-2 mt-1 space-y-0.5 border-l border-[#c45c26]/30 py-1 pl-3',
  subNavItem: (on: boolean) =>
    `w-full rounded-lg px-2 py-2 text-left text-[11px] leading-snug transition ${
      on
        ? 'bg-[#b97845]/20 font-semibold text-[#4a2e1f] dark:bg-[#b97845]/28 dark:text-[#ffe8d4]'
        : 'text-[#8a7058] hover:bg-[#fff8ef] hover:text-[#5a3723] dark:text-[#92857a] dark:hover:bg-white/[0.05] dark:hover:text-[#ddd2c8]'
    }`,
  subIndex: 'mr-1.5 inline-block w-4 tabular-nums text-[#8a7058] dark:text-[#5c534c]',
  main: 'min-w-0 flex-1 overflow-y-auto',
  mainInner: 'mx-auto max-w-6xl px-4 py-6 sm:px-8',
  headerCard:
    'mb-8 flex flex-col gap-4 rounded-2xl border border-[#d8cbb9] bg-[#fffcf7]/95 px-5 py-5 shadow-[0_8px_30px_rgba(44,36,33,0.07)] backdrop-blur-sm dark:border-[#3b2f28] dark:bg-[#1f1814]/95 sm:flex-row sm:items-end sm:justify-between',
  greeting: 'text-sm text-[#7a6a5c] dark:text-[#b9a899]',
  pageTitle:
    "mt-0.5 font-['Fraunces',Georgia,serif] text-2xl font-semibold tracking-tight text-[#1a1411] dark:text-[#f2e6d8] sm:text-[1.75rem]",
  pageSubtitle: 'mt-1 max-w-2xl text-sm leading-relaxed text-[#6b5f57] dark:text-[#b7a697]',
  refreshBtn:
    'shrink-0 rounded-xl bg-gradient-to-r from-[#b97845] to-[#8c4f27] px-5 py-2.5 text-sm font-semibold text-[#fff8f2] shadow-md shadow-[#4a2808]/30 transition hover:brightness-110 active:scale-[0.98]',
  loadingBox:
    'flex min-h-screen items-center justify-center bg-[#fbf4ec] text-[#5c4d42] dark:bg-[#16110d] dark:text-[#d8c8ba] font-sans',
} as const;
